import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../utils/api';
import type { PublicRaffle } from '../types';

interface RaffleEntryFormProps {
  initialCode?: string;
}

function RaffleEntryForm({ initialCode }: RaffleEntryFormProps) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [raffle, setRaffle] = useState<PublicRaffle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Registration form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [joining, setJoining] = useState(false);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  const lookupRaffle = async (entryCode: string) => {
    setLoading(true);
    setError('');
    setRaffle(null);
    setSuccess('');

    try {
      const data = await publicApi.getRaffleByCode(entryCode.trim().toUpperCase());
      setRaffle(data);

      if (data.status === 'completed') {
        setError('This raffle has ended');
      } else if (data.status === 'pending') {
        setError('This raffle is not open for registration yet');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid entry code');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill from initialCode prop (e.g., from URL ?code= param)
  useEffect(() => {
    if (initialCode && initialCode.length === 6) {
      setCode(initialCode.toUpperCase());
      lookupRaffle(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    await lookupRaffle(code);
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!raffle || !firstName.trim() || !lastName.trim() || !email.trim() || !consentGiven) return;

    setJoining(true);
    setError('');

    try {
      const result = await publicApi.joinRaffle(raffle.id, firstName.trim(), lastName.trim(), email.trim());
      setParticipantId(result.participant.id);
      const displayName = firstName.trim().length > 30 ? firstName.trim().substring(0, 30) + '...' : firstName.trim();
      setSuccess(`You're in! Good luck, ${displayName}!`);
      setCountdown(5);
      setFirstName('');
      setLastName('');
      setEmail('');

      // Refresh raffle data to update participant count
      const updated = await publicApi.getRaffleByCode(code);
      setRaffle(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join raffle');
    } finally {
      setJoining(false);
    }
  };

  const handleWatchLive = useCallback(() => {
    if (raffle) {
      const url = participantId
        ? `/live/${raffle.id}?pid=${participantId}`
        : `/live/${raffle.id}`;
      navigate(url);
    }
  }, [raffle, participantId, navigate]);

  // Auto-redirect countdown after successful join
  useEffect(() => {
    if (countdown === null) return;

    if (countdown <= 0) {
      handleWatchLive();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(prev => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, handleWatchLive]);

  return (
    <div className="join-container">
      {!raffle ? (
        <>
          <p className="text-muted" style={{ marginBottom: '2rem' }}>
            Enter the 6-character code to join a raffle
          </p>

          <form onSubmit={handleCodeSubmit} className="code-form">
            <input
              type="text"
              className="form-input"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD12"
              maxLength={6}
              autoFocus
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={code.length !== 6 || loading}
            >
              {loading ? 'Checking...' : 'Enter'}
            </button>
          </form>
        </>
      ) : (
        <>
          <div className="card" style={{ textAlign: 'left' }}>
            <h2>{raffle.name}</h2>
            {raffle.description && (
              <p className="text-muted">{raffle.description}</p>
            )}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <span className={`status-badge status-${raffle.status}`}>
                {raffle.status}
              </span>
              <span className="text-muted">
                {raffle.participant_count} participant{raffle.participant_count !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {success && (
            <div className="success-message">
              <span>{success}</span>
              {countdown !== null && countdown > 0 && (
                <span className="countdown-text">
                  Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
                </span>
              )}
              <button
                className="btn btn-secondary"
                onClick={handleWatchLive}
              >
                Watch Live Drawing
              </button>
            </div>
          )}

          {raffle.status === 'active' && !success && (
            <div className="card" style={{ textAlign: 'left' }}>
              <h3>Enter Your Information</h3>
              <form onSubmit={handleJoin}>
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="John"
                    maxLength={50}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Doe"
                    maxLength={50}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    maxLength={254}
                    required
                  />
                </div>
                <div className="form-group consent-group">
                  <label className="consent-label">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={e => setConsentGiven(e.target.checked)}
                      className="consent-checkbox"
                    />
                    <span className="consent-text">
                      I agree to receive marketing communications from Patch My PC.
                      I understand I can unsubscribe at any time.
                      View our <a href="https://patchmypc.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                    </span>
                  </label>
                </div>
                <button
                  type="submit"
                  className="btn btn-action"
                  disabled={joining || !firstName || !lastName || !email || !consentGiven}
                  style={{ width: '100%', marginTop: '1rem' }}
                >
                  {joining ? 'Joining...' : 'Join Raffle'}
                </button>
              </form>
            </div>
          )}

          {(raffle.status === 'spinning' || raffle.status === 'completed') && (
            <div className="card">
              <p>The drawing is {raffle.status === 'spinning' ? 'in progress' : 'complete'}!</p>
              <button
                className="btn btn-primary"
                onClick={handleWatchLive}
                style={{ marginTop: '1rem' }}
              >
                {raffle.status === 'spinning' ? 'Watch Live' : 'View Results'}
              </button>
            </div>
          )}

          {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}

          <button
            className="btn btn-secondary"
            onClick={() => {
              setRaffle(null);
              setCode('');
              setError('');
              setSuccess('');
              setCountdown(null);
            }}
            style={{ marginTop: '1rem' }}
          >
            Enter Different Code
          </button>
        </>
      )}

      {!raffle && error && <div className="error-message">{error}</div>}
    </div>
  );
}

export default RaffleEntryForm;
